import { useState, useEffect, useCallback, useRef } from "react";
import DOMPurify from "dompurify";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  ArrowLeft, Save, Eye, Plus, Trash2, ArrowUp, ArrowDown, FileDown, Download,
  Heading1, Type, List as ListIcon, Table as TableIcon, PenLine, Minus,
  FileText, Image as ImageIcon, ScrollText, CheckSquare, Layers, ChevronRight
} from "lucide-react";
import HistorialPanel from "../../components/HistorialPanel";

const NUEVO_BLOQUE = {
  documento: { texto: "Empieza a escribir aquí tu documento como en Word.\n\nUsa {{cliente}}, {{evento}}, {{total}}, {{anticipo}}, {{saldo}} y cualquier variable.\n\nDos saltos de línea = nuevo párrafo." },
  heading: { texto: "Nuevo encabezado", nivel: 2 },
  parrafo: { texto: "Escribe aquí. Usa {{cliente}} o cualquier variable disponible." },
  lista: { items: ["Primer ítem", "Segundo ítem"], estilo: "punto" },
  tabla: { primera_fila_header: true, filas: [["Concepto", "Detalle"], ["", ""]] },
  firma: { nombre: "{{cliente}}" },
  separador: {},
  salto_pagina: {},
  imagen: { url: "" },
  seccion: { titulo: "Nueva sección" },
  campo_formulario: { etiqueta: "Nombre del campo" },
  checklist_item: { texto: "Tarea pendiente" },
};

const TIPOS_BLOQUE = [
  { tipo: "documento", label: "Documento (Word)", icon: FileText },
  { tipo: "seccion", label: "Sección", icon: Layers },
  { tipo: "heading", label: "Encabezado", icon: Heading1 },
  { tipo: "parrafo", label: "Párrafo", icon: Type },
  { tipo: "lista", label: "Lista", icon: ListIcon },
  { tipo: "tabla", label: "Tabla", icon: TableIcon },
  { tipo: "checklist_item", label: "Checklist", icon: CheckSquare },
  { tipo: "campo_formulario", label: "Campo formulario", icon: PenLine },
  { tipo: "firma", label: "Firma", icon: ScrollText },
  { tipo: "separador", label: "Separador", icon: Minus },
  { tipo: "salto_pagina", label: "Salto de página", icon: FileText },
  { tipo: "imagen", label: "Imagen (URL)", icon: ImageIcon },
];

const PlantillaBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plantilla, setPlantilla] = useState(null);
  const [bloques, setBloques] = useState([]);
  const [variables, setVariables] = useState({ sistema: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(true);
  const [showHistorial, setShowHistorial] = useState(false);
  const previewTimer = useRef(null);
  // Iter154 · Editor visual directo (contentEditable)
  const visualRef = useRef(null);
  const [visualInit, setVisualInit] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, vRes] = await Promise.all([
        axios.get(`${API}/plantillas/${id}`),
        axios.get(`${API}/plantillas/variables-disponibles`),
      ]);
      setPlantilla(pRes.data);
      setBloques((pRes.data.bloques || []).sort((a, b) => (a.orden || 0) - (b.orden || 0)));
      setVariables(vRes.data);
    } catch {
      toast.error("Error al cargar plantilla");
      navigate("/panel/plantillas");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Iter154 · Sincroniza el editor visual con `plantilla.contenido_html` una
  // sola vez al cargar (evita perder el cursor al reescribir mientras se edita).
  useEffect(() => {
    if (!visualInit && plantilla && visualRef.current) {
      visualRef.current.innerHTML = plantilla.contenido_html || "";
      setVisualInit(true);
    }
  }, [plantilla, visualInit]);

  // Debounced live preview
  const generarPreview = useCallback(async () => {
    if (!plantilla) return;
    try {
      const res = await axios.post(`${API}/plantillas/${id}/preview`, {});
      setPreviewHtml(res.data.html || "");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[PlantillaBuilder] Preview falló:", err?.message);
    }
  }, [id, plantilla]);

  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(generarPreview, 600);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [bloques, generarPreview]);

  const guardarPlantilla = async (toastMsg = "Plantilla guardada") => {
    if (!plantilla) return;
    setGuardando(true);
    try {
      const bloquesConOrden = bloques.map((b, i) => ({ ...b, orden: i }));
      await axios.put(`${API}/plantillas/${id}`, {
        nombre: plantilla.nombre,
        tipo: plantilla.tipo,
        tipo_evento: plantilla.tipo_evento || "",
        descripcion: plantilla.descripcion || "",
        bloques: bloquesConOrden,
        variables_extra: plantilla.variables_extra || [],
        contenido_html: plantilla.contenido_html || "",
        activa: plantilla.activa !== false,
      });
      toast.success(toastMsg);
    } catch {
      toast.error("Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const agregarBloque = (tipo) => {
    setBloques(prev => [
      ...prev,
      {
        bloque_id: `blq_${Math.random().toString(36).slice(2, 10)}`,
        tipo,
        orden: prev.length,
        props: { ...(NUEVO_BLOQUE[tipo] || {}) },
      }
    ]);
  };

  const actualizarBloque = (i, props) => {
    setBloques(prev => prev.map((b, idx) => idx === i ? { ...b, props: { ...b.props, ...props } } : b));
  };

  const moverBloque = (i, dir) => {
    setBloques(prev => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const eliminarBloque = (i) => {
    setBloques(prev => prev.filter((_, idx) => idx !== i));
  };

  const insertarVariableEn = (i, clave) => {
    setBloques(prev => prev.map((b, idx) => {
      if (idx !== i) return b;
      const tipo = b.tipo;
      if (tipo === "documento" || tipo === "heading" || tipo === "parrafo" || tipo === "checklist_item") {
        return { ...b, props: { ...b.props, texto: (b.props.texto || "") + ` {{${clave}}}` } };
      }
      if (tipo === "seccion") {
        return { ...b, props: { ...b.props, titulo: (b.props.titulo || "") + ` {{${clave}}}` } };
      }
      if (tipo === "campo_formulario") {
        return { ...b, props: { ...b.props, etiqueta: (b.props.etiqueta || "") + ` {{${clave}}}` } };
      }
      if (tipo === "firma") {
        return { ...b, props: { ...b.props, nombre: (b.props.nombre || "") + ` {{${clave}}}` } };
      }
      return b;
    }));
  };

  const descargarPDF = async () => {
    await guardarPlantilla("Plantilla guardada antes de generar PDF");
    try {
      const res = await axios.post(`${API}/plantillas/${id}/generar-pdf`, {});
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${res.data.pdf_base64}`;
      link.download = res.data.filename || "plantilla.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF descargado");
    } catch {
      toast.error("Error al generar PDF");
    }
  };

  if (loading || !plantilla) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/panel/plantillas")} className="border-stone-300" data-testid="btn-volver-plantillas">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div>
            <Input
              value={plantilla.nombre}
              onChange={(e) => setPlantilla(p => ({ ...p, nombre: e.target.value }))}
              className="text-xl font-display border-0 px-0 h-9 focus-visible:ring-0"
              data-testid="input-nombre-builder"
            />
            <p className="text-xs text-stone-500">{plantilla.tipo}{plantilla.tipo_evento ? ` · ${plantilla.tipo_evento}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistorial(s => !s)} className="border-stone-300" data-testid="btn-historial-plantilla">
            Historial
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(p => !p)} className="border-stone-300" data-testid="btn-toggle-preview">
            <Eye className="w-4 h-4 mr-1" /> {previewOpen ? "Ocultar preview" : "Vista previa"}
          </Button>
          <Button variant="outline" size="sm" onClick={descargarPDF} className="border-stone-300" data-testid="btn-pdf-plantilla">
            <FileDown className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!window.confirm("¿Restaurar el diseño por defecto? Se descartará el HTML personalizado de esta plantilla.")) return;
              setPlantilla(p => ({ ...p, contenido_html: "", activa: false }));
              if (visualRef.current) visualRef.current.innerHTML = "";
              toast.success("Restaurado al diseño por defecto");
            }}
            className="border-stone-300"
            data-testid="btn-restaurar-plantilla"
          >
            Restaurar
          </Button>
          <Button size="sm" onClick={() => guardarPlantilla()} disabled={guardando} className="btn-primary" data-testid="btn-guardar-plantilla">
            <Save className="w-4 h-4 mr-1" /> {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>

      {showHistorial && (
        <HistorialPanel coleccion="plantillas_custom" docId={id} titulo="Cambios de esta plantilla" />
      )}

      {/* ─── Iter154 · EDITOR VISUAL DIRECTO (tipo Word) ─── */}
      <Card className="border-stone-200">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-stone-200 bg-stone-50 sticky top-0 z-10" data-testid="plantilla-toolbar">
            {[
              { cmd: "bold",           label: "N", title: "Negrita", style: { fontWeight: 700 } },
              { cmd: "italic",         label: "I", title: "Cursiva", style: { fontStyle: "italic" } },
              { cmd: "formatBlock:h1", label: "H1", title: "Título" },
              { cmd: "formatBlock:h2", label: "H2", title: "Subtítulo" },
              { cmd: "formatBlock:p",  label: "¶", title: "Párrafo" },
              { cmd: "insertUnorderedList", label: "•", title: "Lista con viñetas" },
              { cmd: "insertOrderedList",   label: "1.", title: "Lista numerada" },
              { cmd: "justifyLeft",  label: "⇤", title: "Alinear izquierda" },
              { cmd: "justifyCenter", label: "≡", title: "Centrar" },
              { cmd: "justifyRight", label: "⇥", title: "Alinear derecha" },
            ].map(t => (
              <button
                key={t.cmd}
                type="button"
                title={t.title}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (t.cmd.startsWith("formatBlock:")) {
                    document.execCommand("formatBlock", false, t.cmd.split(":")[1]);
                  } else {
                    document.execCommand(t.cmd, false, null);
                  }
                  if (visualRef.current) {
                    setPlantilla(p => ({ ...p, contenido_html: visualRef.current.innerHTML, activa: true }));
                  }
                }}
                className="px-2.5 py-1 text-xs rounded border border-stone-300 hover:bg-stone-100"
                style={t.style}
                data-testid={`tb-${t.cmd}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div
            ref={visualRef}
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => {
              const html = e.currentTarget ? e.currentTarget.innerHTML : "";
              setPlantilla(p => ({ ...(p || {}), contenido_html: html, activa: true }));
            }}
            className="min-h-[420px] px-4 py-5 outline-none prose prose-stone max-w-none"
            style={{ fontFamily: "'Cormorant Garamond','Georgia',serif", fontSize: "12pt", lineHeight: 1.55, background: "#fbf9f3", color: "#2b2b2b" }}
            data-testid="plantilla-editor-visual"
          />
        </CardContent>
      </Card>

      {/* ─── Iter154 · OPCIONES AVANZADAS (Modo HTML crudo + Bloques + Variables) ─── */}
      <details className="rounded-xl border border-stone-200 bg-white" data-testid="opciones-avanzadas">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 flex items-center justify-between">
          <span>⚙︎ Opciones avanzadas</span>
          <span className="text-xs text-stone-500">HTML crudo · Constructor por bloques · Variables</span>
        </summary>
        <div className="p-4 space-y-4 border-t border-stone-200">
      <details className="mb-4 rounded-xl border border-stone-200 bg-white">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 flex items-center justify-between" data-testid="toggle-html-avanzado">
          <span>🎨 Modo HTML avanzado <span className="text-xs text-stone-500 font-normal">— reemplaza el diseño default del PDF profesional</span></span>
          <span className="text-xs text-stone-500">{(plantilla.contenido_html || "").length ? "✓ HTML personalizado" : "vacío (usará el diseño default)"}</span>
        </summary>
        <div className="p-4 border-t border-stone-200 space-y-3">
          <div className="flex items-start gap-3">
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input
                type="checkbox"
                checked={plantilla.activa !== false}
                onChange={(e) => setPlantilla(p => ({ ...p, activa: e.target.checked }))}
                className="rounded"
                data-testid="check-plantilla-activa"
              />
              <strong>Aplicar esta plantilla</strong> cuando se genere un PDF de tipo <code className="bg-stone-100 px-1.5 rounded">{plantilla.tipo}</code>
            </label>
          </div>
          <p className="text-xs text-stone-600">
            Escribe HTML/CSS libre. Usa <code className="bg-stone-100 px-1 rounded">{"{{ cliente.nombre }}"}</code>,
            <code className="bg-stone-100 px-1 rounded"> {"{{ evento.fecha }}"} </code>,
            <code className="bg-stone-100 px-1 rounded"> {"{{ totales.total }}"} </code>,
            <code className="bg-stone-100 px-1 rounded"> {"{{ totales.anticipo }}"} </code>,
            <code className="bg-stone-100 px-1 rounded"> {"{{ totales.saldo }}"} </code>,
            <code className="bg-stone-100 px-1 rounded"> {"{{ hoy }}"} </code>.
            Los datos se auto-poblan del evento.
          </p>
          <textarea
            value={plantilla.contenido_html || ""}
            onChange={(e) => setPlantilla(p => ({ ...p, contenido_html: e.target.value }))}
            placeholder="<html>&#10;<head><style>body{font-family:Georgia,serif;padding:40px}</style></head>&#10;<body>&#10;  <h1>Contrato de {{ cliente.nombre }}</h1>&#10;  <p>Fecha del evento: {{ evento.fecha }}</p>&#10;  <p>Total: ${{ totales.total }} MXN</p>&#10;</body>&#10;</html>"
            className="w-full font-mono text-xs p-3 border border-stone-300 rounded-lg resize-y"
            rows={12}
            data-testid="textarea-html-avanzado"
          />
        </div>
      </details>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Sidebar: bloques + variables */}
        <aside className="lg:col-span-3 space-y-4">
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <Label className="text-xs uppercase tracking-wide text-stone-500">Agregar bloque</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {TIPOS_BLOQUE.map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.tipo}
                      onClick={() => agregarBloque(t.tipo)}
                      className="flex flex-col items-center gap-1 p-2 border border-stone-200 rounded-sm hover:border-emerald-700 hover:bg-emerald-50 transition text-xs text-stone-700"
                      data-testid={`btn-add-${t.tipo}`}
                    >
                      <Icon className="w-4 h-4 text-emerald-800" />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardContent className="p-4">
              <Label className="text-xs uppercase tracking-wide text-stone-500">Variables disponibles</Label>
              <p className="text-[11px] text-stone-500 mt-1 mb-2">Toca una variable para copiarla. Pégala donde quieras con <code className="text-[10px] bg-stone-100 px-1">{"{{clave}}"}</code></p>
              <div className="max-h-72 overflow-y-auto space-y-1">
                {variables.sistema.map(v => (
                  <button
                    key={v.clave}
                    onClick={() => { navigator.clipboard.writeText(`{{${v.clave}}}`); toast.success(`Copiado: {{${v.clave}}}`); }}
                    className="w-full text-left text-xs px-2 py-1 rounded hover:bg-stone-100 flex items-center justify-between"
                    data-testid={`var-${v.clave}`}
                  >
                    <span className="text-stone-700">{v.etiqueta}</span>
                    <code className="text-[10px] text-emerald-700">{`{{${v.clave}}}`}</code>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main: bloques editables */}
        <main className={`${previewOpen ? "lg:col-span-5" : "lg:col-span-9"} space-y-3`}>
          {bloques.length === 0 && (
            <Card className="border-dashed border-2 border-stone-200">
              <CardContent className="p-8 text-center">
                <Layers className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">Agrega bloques desde el panel izquierdo para empezar tu plantilla.</p>
              </CardContent>
            </Card>
          )}
          {bloques.map((b, i) => (
            <Card key={b.bloque_id || i} className="border-stone-200" data-testid={`bloque-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wide text-stone-400 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" /> {b.tipo.replace("_", " ")}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moverBloque(i, -1)} title="Subir" data-testid={`btn-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moverBloque(i, 1)} title="Bajar" data-testid={`btn-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => eliminarBloque(i)} title="Eliminar" data-testid={`btn-del-bloque-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <BloqueEditor bloque={b} onChange={(props) => actualizarBloque(i, props)} onInsertarVariable={() => insertarVariableEn(i, "cliente")} />
              </CardContent>
            </Card>
          ))}
        </main>

        {/* Preview pane */}
        {previewOpen && (
          <aside className="lg:col-span-4">
            <Card className="border-stone-200 sticky top-4">
              <CardContent className="p-4">
                <Label className="text-xs uppercase tracking-wide text-stone-500 flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Vista previa
                </Label>
                <p className="text-[10px] text-stone-400 mb-2">Las variables muestran ejemplos. Conecta a un evento real para ver datos reales.</p>
                <div
                  className="prose prose-sm max-w-none p-4 bg-white border border-stone-100 rounded-sm overflow-y-auto"
                  style={{ maxHeight: "calc(100vh - 200px)" }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml || "<p style='color:#999;font-style:italic'>Sin contenido aún.</p>", { ADD_ATTR: ["style"] }) }}
                />
              </CardContent>
            </Card>
          </aside>
        )}
      </div>
        </div>
      </details>
    </div>
  );
};

// ============== EDITOR POR BLOQUE ==============
const BloqueEditor = ({ bloque, onChange }) => {
  const { tipo, props = {} } = bloque;

  if (tipo === "documento") {
    return (
      <Textarea
        value={props.texto || ""}
        onChange={(e) => onChange({ texto: e.target.value })}
        placeholder="Escribe tu documento como en Word. Usa {{variable}} donde quieras."
        rows={20}
        className="border-stone-300 font-mono text-sm leading-relaxed"
        data-testid="bloque-documento-texto"
      />
    );
  }
  if (tipo === "heading") {
    return (
      <div className="space-y-2">
        <Input value={props.texto || ""} onChange={(e) => onChange({ texto: e.target.value })} placeholder="Texto del encabezado" className="border-stone-300 text-lg font-medium" data-testid="bloque-heading-texto" />
        <Select value={String(props.nivel || 2)} onValueChange={(v) => onChange({ nivel: parseInt(v) })}>
          <SelectTrigger className="border-stone-300 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">H1 grande</SelectItem>
            <SelectItem value="2">H2 medio</SelectItem>
            <SelectItem value="3">H3 pequeño</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (tipo === "parrafo") {
    return (
      <Textarea
        value={props.texto || ""}
        onChange={(e) => onChange({ texto: e.target.value })}
        placeholder="Escribe el contenido. Usa {{variable}} para insertar datos dinámicos."
        rows={5}
        className="border-stone-300"
        data-testid="bloque-parrafo-texto"
      />
    );
  }
  if (tipo === "seccion") {
    return (
      <Input value={props.titulo || ""} onChange={(e) => onChange({ titulo: e.target.value })} placeholder="Título de la sección" className="border-stone-300 font-medium" />
    );
  }
  if (tipo === "lista") {
    const items = props.items || [];
    return (
      <div className="space-y-2">
        <Select value={props.estilo || "punto"} onValueChange={(v) => onChange({ estilo: v })}>
          <SelectTrigger className="border-stone-300 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="punto">Viñetas</SelectItem>
            <SelectItem value="numero">Numerada</SelectItem>
          </SelectContent>
        </Select>
        {items.map((it, idx) => (
          <div key={`pb-item-${idx}`} className="flex gap-2">
            <Input value={it} onChange={(e) => { const next = [...items]; next[idx] = e.target.value; onChange({ items: next }); }} placeholder={`Ítem ${idx + 1}`} className="border-stone-300" />
            <Button size="sm" variant="ghost" onClick={() => onChange({ items: items.filter((_, i) => i !== idx) })} className="text-red-600">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => onChange({ items: [...items, ""] })} className="border-stone-300 text-xs">
          <Plus className="w-3 h-3 mr-1" /> Agregar ítem
        </Button>
      </div>
    );
  }
  if (tipo === "tabla") {
    const filas = props.filas || [];
    return (
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs text-stone-600">
          <input type="checkbox" checked={!!props.primera_fila_header} onChange={(e) => onChange({ primera_fila_header: e.target.checked })} />
          Primera fila como encabezado
        </label>
        <div className="space-y-2">
          {filas.map((fila, i) => (
            <div key={`pb-row-${i}`} className="flex gap-2 items-center">
              {fila.map((celda, j) => (
                <Input
                  key={`pb-cell-${i}-${j}`}
                  value={celda}
                  onChange={(e) => {
                    const next = filas.map((f, ii) => f.map((c, jj) => (ii === i && jj === j) ? e.target.value : c));
                    onChange({ filas: next });
                  }}
                  className="border-stone-300 text-sm"
                  placeholder={i === 0 && props.primera_fila_header ? "Encabezado" : "Celda"}
                />
              ))}
              <Button size="sm" variant="ghost" onClick={() => {
                const next = filas.filter((_, idx) => idx !== i);
                onChange({ filas: next });
              }} className="text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => {
              const cols = filas[0]?.length || 2;
              onChange({ filas: [...filas, Array(cols).fill("")] });
            }} className="border-stone-300 text-xs">
              <Plus className="w-3 h-3 mr-1" /> Fila
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              const next = filas.map(f => [...f, ""]);
              onChange({ filas: next });
            }} className="border-stone-300 text-xs">
              <Plus className="w-3 h-3 mr-1" /> Columna
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (tipo === "firma") {
    return <Input value={props.nombre || ""} onChange={(e) => onChange({ nombre: e.target.value })} placeholder="Nombre del firmante" className="border-stone-300" />;
  }
  if (tipo === "imagen") {
    return <Input value={props.url || ""} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://imagen.png" className="border-stone-300" />;
  }
  if (tipo === "campo_formulario") {
    return <Input value={props.etiqueta || ""} onChange={(e) => onChange({ etiqueta: e.target.value })} placeholder="Etiqueta del campo" className="border-stone-300" />;
  }
  if (tipo === "checklist_item") {
    return <Input value={props.texto || ""} onChange={(e) => onChange({ texto: e.target.value })} placeholder="Tarea o ítem a marcar" className="border-stone-300" />;
  }
  if (tipo === "separador") return <p className="text-xs text-stone-400 italic">Línea horizontal — sin opciones.</p>;
  if (tipo === "salto_pagina") return <p className="text-xs text-stone-400 italic">Salto de página — sin opciones.</p>;
  return <p className="text-xs text-stone-400">Bloque sin configuración.</p>;
};

export default PlantillaBuilder;
